import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';

const ACTIVE_CLASS = 'label-print-active';
const STYLE_ATTRIBUTE = 'data-label-print-style';

/**
 * Renders print-only output into a body-level element so the dashboard (and any modal) can be
 * hidden under @media print without touching their markup. `rootRef` receives the element, which
 * `printSheets` marks active while printing. Hidden on screen by LabelPrinter.css.
 */
export default function LabelPrintPortal({children, rootRef}) {
  const [root] = useState(() => {
    const element = document.createElement('div');
    element.className = 'label-print-root';
    return element;
  });

  useEffect(() => {
    document.body.appendChild(root);
    if (rootRef) rootRef.current = root;
    return () => {
      if (rootRef) rootRef.current = null;
      root.remove();
    };
  }, [root, rootRef]);

  return createPortal(children, root);
}

/**
 * Prints the contents of a LabelPrintPortal root at an exact paper size. Injects a scoped
 * `@page` rule plus print rules that hide every other body child, then removes both after the
 * print dialog closes.
 *
 * @param {{root: HTMLElement, width: number, height: number}} args paper size in inches
 */
export function printSheets({root, width, height}) {
  if (!root) return;
  document.querySelectorAll(`style[${STYLE_ATTRIBUTE}]`).forEach(stale => stale.remove());

  const style = document.createElement('style');
  style.setAttribute(STYLE_ATTRIBUTE, 'true');
  style.textContent = [
    `@page { size: ${Number(width)}in ${Number(height)}in; margin: 0; }`,
    '@media print {',
    `  body > :not(.${ACTIVE_CLASS}) { display: none !important; }`,
    `  body > .${ACTIVE_CLASS} { display: block !important; }`,
    '}',
  ].join('\n');
  document.head.appendChild(style);
  root.classList.add(ACTIVE_CLASS);

  const cleanup = () => {
    window.removeEventListener('afterprint', cleanup);
    root.classList.remove(ACTIVE_CLASS);
    style.remove();
  };
  window.addEventListener('afterprint', cleanup);

  try {
    window.print();
  } catch (error) {
    cleanup();
    throw error;
  }
}
